import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Store } from '@ngxs/store';
import { SequenceJsonModel } from '../../sequence.json.model';
import { IFilters } from '@datx/jsonapi/interfaces/IFilters';
import { getMinFilterLengthText } from '../../../core/util';
import { FreeFilterInputComponent } from '../../../utils/component/free-filter-input/free-filter-input.component';
import { PlayerMessageService } from '../../../core/services/player.service';
import { getStatusColor, getStatusText, StateFilterCBComponent } from '../../../utils/component/state-filter-combobox/state-cb.component';
import { Approval } from '../../../core/types';
import { SequenceEntityService } from '../../services/sequence.entity.service';
import { ConsumerEntityService } from '../../../consumer/services/consumer.entity.service';
import { SubjectEntityService } from '../../../subject/services/subject.entity.service';
import { BehaviorSubject, combineLatest, firstValueFrom, from, map, Observable, switchMap, tap } from 'rxjs';
import { SequenceEntity } from '../../sequence.entity';
import { makeQueryKey } from '../../../utils/entity-states/query-key';
import { EntityStateModel, InvalidateLoadedQueries } from '../../../core/state/entity.state';
import { ConsumerEntity } from '../../../consumer/consumer.entity';
import { SubjectEntity } from '../../../subject/subject.entity';
import { WidgetMessageService } from '../../../core/services/widget-message.service';
import { MatDialog } from '@angular/material/dialog';
import { SequenceEditComponent } from '../../sequence-edit/sequence-edit.component';
import { ConfirmDialogComponent } from '../../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { SimpleConfirmData } from '../../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { TranslocoModule } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableLoadingSpinnerComponent } from '../../../utils/component/table-loading-spinner/table-loading-spinner';
import { LocalStorageService } from '../../../core/services/local-storage.service';

@Component({
  selector: 'pixl-exam-sequence-main',
  imports: [
    MatIcon,
    MatSortModule,
    MatIconModule,
    MatTableModule,
    MatButtonModule,
    MatTooltip,
    MatSelectModule,
    DatePipe,
    FreeFilterInputComponent,
    StateFilterCBComponent,
    NgClass,
    AsyncPipe,
    MatPaginator,
    TableLoadingSpinnerComponent,
    TranslocoModule
  ],
  templateUrl: './exam-sequence-main.component.html',
  styleUrl: './exam-sequence-main.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExamSequenceMainComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  pageSize = 20;
  pageIndex = 0;
  totalItems = 1000;

  private store = inject(Store);
  private examSequenceService = inject(SequenceEntityService);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private widgetService = inject(WidgetMessageService);
  private localStorageService = inject(LocalStorageService);
  examSequences$!: Observable<SequenceEntity[]>;
  private dialog = inject(MatDialog);
  deletedIds$ = new BehaviorSubject<string[]>([]);
  private refresh$ = new BehaviorSubject<void>(undefined);
  private pageChange$ = new BehaviorSubject<{ pageIndex: number, pageSize: number }>({ pageIndex: 0, pageSize: this.pageSize });

  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;

  public cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private playerService = inject(PlayerMessageService);

  public filters: IFilters = {};
  private freeFilter = '';
  private consumerFilter: string[] = [];
  private subjectFilter: string[] = [];
  private statusFilter = '';
  public readonly freeFilterLen = 3;

  public displayedColumns: string[] = ['edit-actions', 'id', 'name', 'consumer', 'subject', 'tasks', 'state', 'createdAt'];
  public dataSource = new MatTableDataSource<SequenceJsonModel>([] as SequenceJsonModel[]);
  public currentSelectedRow = signal<string | undefined>(undefined);
  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;

  ngOnInit() {
    this.loadRelationData();

    this.examSequences$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query: Record<string, string | number> = {
          'include': 'consumers,subjects,exams',
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'filter[kind]': 'exam',
          'sort': 'name'
        };

        if (this.freeFilter?.trim()?.length >= 3) {
          query['page[number]'] = 0;
          query['filter[nameLike]'] = this.freeFilter.trim();
        }

        if (this.statusFilter?.length > 0) {
          query['page[number]'] = 0;
          query['filter[approval]'] = this.statusFilter.trim();
        }

        if (this.consumerFilter?.length > 0) {
          query['page[number]'] = 0;
          query['filter[consumers][id]'] = this.consumerFilter.join(',');
        }

        if (this.subjectFilter?.length > 0) {
          query['page[number]'] = 0;
          query['filter[subjectId]'] = this.subjectFilter.join(',');
        }

        return from(this.examSequenceService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.examSequenceService.allByQuery(query))),
          map((examSequences: SequenceEntity[]) => examSequences.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            const state = this.store.selectSnapshot(this.examSequenceService.stateClass) as EntityStateModel<SequenceEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state.meta?.[queryKey]?.page?.total ?? 0;
          })
        );
      })
    );
  }

  ngAfterViewInit(): void {
    this.paginator.page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: PageEvent) => {
      this.pageChange$.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  private loadRelationData(): void {
    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery))
      )
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(
      this.subjectService.ensureLoaded(subjectQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery))
      )
    );
  }

  addExamSequence(): void {
    const dialogRef = this.dialog.open(SequenceEditComponent, {
      width: '500px',
      data: {
        kind: 'exam'
      }
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<SequenceEntity> | undefined) => {
      if (result) {
        const newEntityInstance = Object.assign(new SequenceEntity(), result);
        await this.examSequenceService.create(newEntityInstance);
        this.store.dispatch(new InvalidateLoadedQueries());
        this.refresh$.next();

        this.widgetService.setMessage({
          message: 'Testsequenz erfolgreich erstellt',
          messageTye: 'mtSuccess'
        });
      }
    });
  }

  public async copyExamSequence(examSequence: SequenceEntity) {
    const copyEntityInstance = Object.assign(new SequenceEntity(), examSequence);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (copyEntityInstance as any).id = undefined;
    copyEntityInstance.name = `${copyEntityInstance.name} (Kopie)`;
    copyEntityInstance.version = 1;
    await this.examSequenceService.create(copyEntityInstance);
    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: 'Testsequenz erfolgreich dupliziert.',
      messageTye: 'mtSuccess'
    });
  }

  public async deleteExamSequence(examSequence: SequenceEntity): Promise<void> {
    if (!examSequence?.id) {
      console.error('ID is undefined!', examSequence);
      return;
    }

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogComponent, SimpleConfirmData, boolean>(ConfirmDialogComponent, {
          data: {
            name: String(examSequence.name),
            headerText: 'Testsequenz löschen'
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(this.examSequenceService.delete(examSequence.id));

    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: 'Testsequenz erfolgreich gelöscht.',
      messageTye: 'mtSuccess'
    });
  }

  editExamSequence(examSequence: SequenceEntity): void {
    const dialogRef = this.dialog.open(SequenceEditComponent, {
      width: '800px',
      data: { ...examSequence }
    });

    dialogRef.afterClosed().subscribe(async (updatedExamSequence: SequenceEntity | undefined) => {
      if (!updatedExamSequence) return;
      await firstValueFrom(this.examSequenceService.update(updatedExamSequence));
      this.refresh$.next();

      this.widgetService.setMessage({
        message: 'Testsequenz erfolgreich editiert',
        messageTye: 'mtSuccess'
      });
    });
  }

  getConsumerName(examSequenceConsumerId: string) {
    if (!examSequenceConsumerId) return '---';

    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    const consumer = this.consumerService.stateClass.byId(state)(examSequenceConsumerId);

    return consumer?.name || examSequenceConsumerId;
  }

  getSubjectName(examSequenceSubjectId: string) {
    if (!examSequenceSubjectId) return '---';

    const state = this.store.selectSnapshot(this.subjectService.stateClass);
    const subject = this.subjectService.stateClass.byId(state)(examSequenceSubjectId);

    return subject?.name || examSequenceSubjectId;
  }

  getAmountOfTasks(examSequenceList: string[]): number {
    let result = 0;
    examSequenceList.forEach(item => {
      result += item.length;
    });
    return result;
  }

  getCreationDate(examSequenceId: string): Date {
    return new Date(parseInt(examSequenceId.substring(0, 8), 16) * 1000);
  }

  public getSearchFilterHint(): string {
    return getMinFilterLengthText(this.freeFilterLen);
  }

  public setFreeFilter(input: string): void {
    this.currentSelectedRow.set(undefined); // unmark the select row
    this.freeFilter = input;
    this.refresh$.next();
  }

  public setConsumerFilter(selected: string[]): void {
    this.currentSelectedRow.set(undefined); // unmark the select row
    this.consumerFilter = selected;
    this.refresh$.next();
  }

  public setSubjectFilter(selected: string[]): void {
    this.currentSelectedRow.set(undefined); // unmark the select row
    this.subjectFilter = selected;
    this.refresh$.next();
  }

  public setStatusFilter(status: string): void {
    this.currentSelectedRow.set(undefined); // unmark the select row
    this.statusFilter = status;
    this.refresh$.next();
  }

  public getStatusColor(approval: Approval): string {
    return getStatusColor(approval);
  }

  public getStatusText(approval: Approval): string {
    return getStatusText(approval);
  }

  trackById(index: number, examSequence: SequenceEntity): string {
    return examSequence.id;
  }

  public previewPlayer2(sequence: SequenceJsonModel) {
    this.currentSelectedRow.set(sequence.id);
    this.playerService.openPlayerPreview(sequence.consumerId, 'exam_sequence', sequence.id);
  }
}
